import React, { Suspense } from "react";
import { Link, useLocation } from "react-router-dom";
import { XOPublicRouteErrorBoundary } from "../XOPublicRouteErrorBoundary";
import { XOPublicQuietLoading } from "../XOPublicQuietLoading";
import { ContinuityTraversalStrip } from "./ContinuityTraversalStrip";
import { ContinuityCapsule } from "./ContinuityCapsule";
import { AmbientCanonicalization } from "./AmbientCanonicalization";
import { CanonicallsContinuityCarryProvider } from "./CanonicallsContinuityCarryProvider";
import { ContinuityAnchor } from "./ContinuityAnchor";
import {
  CANONICALLS_BRIDGE,
  CANONICALLS_SOURCE_PRINCIPLE,
} from "../../lib/canonicallsCopy";
import { CANONICALLS_TOP_NAV, navAriaCurrent } from "../../lib/canonicallsNavigation";
import "./CanonicallsShell.css";

function CanonicallsShellInner({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  return (
    <div className="canonicalls-shell">
      <header className="canonicalls-shell__header">
        <div className="canonicalls-shell__header-inner">
          <Link to="/" className="canonicalls-shell__brand">
            <span className="canonicalls-shell__brand-name">Canonicalls</span>
            <span className="canonicalls-shell__brand-line">Continuity surviving traversal</span>
          </Link>
          <nav className="canonicalls-shell__nav" aria-label="Canonicalls">
            {CANONICALLS_TOP_NAV.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                aria-current={navAriaCurrent(pathname, item.to)}
                className={item.cta ? "canonicalls-shell__nav-cta" : undefined}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      </header>
      <div className="canonicalls-provenance" aria-hidden />
      <div className="canonicalls-shell__carry-rail">
        <ContinuityCapsule />
        <ContinuityTraversalStrip />
      </div>
      <main className="canonicalls-shell__main">{children}</main>
      <footer className="canonicalls-shell__footer">
        <AmbientCanonicalization variant="footer" />
        <ContinuityAnchor subtle />
        <p className="canonicalls-shell__footer-principle">{CANONICALLS_SOURCE_PRINCIPLE}</p>
        <p className="canonicalls-shell__footer-bridge">
          {CANONICALLS_BRIDGE.footerVerify}{" "}
          <a
            href="https://verify.xo-vault.com/vault/verify.html?traverse=1&source=canonicalls"
            target="_blank"
            rel="noreferrer"
          >
            {CANONICALLS_BRIDGE.archiveLabel}
          </a>
        </p>
        <p className="canonicalls-shell__footer-return">{CANONICALLS_BRIDGE.returnHint}</p>
      </footer>
    </div>
  );
}

export function CanonicallsShell({ children }: { children: React.ReactNode }) {
  return (
    <CanonicallsContinuityCarryProvider>
      <CanonicallsShellInner>{children}</CanonicallsShellInner>
    </CanonicallsContinuityCarryProvider>
  );
}

export function CanonicallsShellWithSuspense({
  children,
  loadingLabel = "Loading…",
}: {
  children: React.ReactNode;
  loadingLabel?: string;
}) {
  return (
    <CanonicallsShell>
      <XOPublicRouteErrorBoundary>
        <Suspense
          fallback={
            <div className="canonicalls-quiet-loading">
              <XOPublicQuietLoading label={loadingLabel} />
            </div>
          }
        >
          {children}
        </Suspense>
      </XOPublicRouteErrorBoundary>
    </CanonicallsShell>
  );
}
